import { useEffect, useState } from 'react'

/**
 * Tracks browser/network connectivity. Combines `navigator.onLine` with the
 * `online`/`offline` events so React components can react when the user loses
 * or regains internet — used to warn before End Meeting (BRAI-140).
 *
 * Note: `navigator.onLine` only reflects the OS link state. We intentionally
 * keep this lightweight (no health-ping) to avoid extra traffic; downstream
 * code still has to handle "online flag is true but request fails" gracefully.
 *
 * @returns `{ isOnline }` — `true` while the browser reports connectivity.
 */
export function useOnlineStatus(): { isOnline: boolean } {
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  )

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    setIsOnline(typeof navigator === 'undefined' ? true : navigator.onLine)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return { isOnline }
}
